// Zweck: Aufträge auch in großen Listen ohne langes Scrollen finden.
// Kontext: Suche in lokal gespeicherten Lagerdaten, auch offline. Stand: 20.10.2026.
import { useId, useMemo, useRef, useState } from 'react';
import type { WarehouseProject } from '../interfaces/warehouse';

const normalize = (value: string) =>
  value
    .toLocaleLowerCase('de')
    .replace(/ae/g, 'ä')
    .replace(/oe/g, 'ö')
    .replace(/ue/g, 'ü')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

export default function WarehouseProjectPicker({
  projects,
  value,
  optional,
  disabled,
  allowWithoutProject = true,
  onChange,
}: {
  projects: WarehouseProject[];
  value: string;
  optional: boolean;
  disabled: boolean;
  allowWithoutProject?: boolean;
  onChange: (value: string) => void;
}) {
  const [query, setQuery] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const index = useMemo(
    () =>
      projects.map((project) => ({
        project,
        text: normalize(`${project.name} ${project.id}`),
        key: normalize(project.id),
      })),
    [projects],
  );
  const normalizedQuery = normalize(query);
  const terms = normalizedQuery.split(/\s+/).filter(Boolean);
  const matches = index
    .filter((entry) => terms.every((term) => entry.text.includes(term)))
    .sort(
      (a, b) =>
        Number(b.key === normalizedQuery) - Number(a.key === normalizedQuery) ||
        a.project.name.localeCompare(b.project.name, 'de') ||
        a.project.id.localeCompare(b.project.id, 'de', { numeric: true }),
    );
  const selected = projects.find((project) => project.id === value);
  const choose = (next: string) => {
    setQuery('');
    onChange(next);
  };
  return (
    <div className="wh-project-picker">
      {value ? (
        <div
          className="wh-project-selected"
          role="group"
          aria-label="Gewählter Auftrag"
        >
          <span className="wh-field-hint">Gewählter Auftrag</span>
          <strong>
            {value === 'without-project'
              ? 'Ohne Auftrag'
              : (selected?.name ?? value)}
          </strong>
          {selected && <span>Auftrags-ID: {selected.id}</span>}
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              choose('');
              requestAnimationFrame(() => input.current?.focus());
            }}
          >
            Auftrag ändern
          </button>
        </div>
      ) : (
        <>
          <label htmlFor={id}>
            Auftrag suchen {optional ? '(optional)' : ''}
          </label>
          <div className="wh-search-field">
            <input
              ref={input}
              id={id}
              type="search"
              autoCapitalize="none"
              autoCorrect="off"
              enterKeyHint="search"
              placeholder="Name oder Auftrags-ID"
              autoComplete="off"
              spellCheck={false}
              value={query}
              disabled={disabled}
              aria-describedby={`${id}-hint`}
              onChange={(event) => setQuery(event.target.value)}
            />
            {query && (
              <button
                type="button"
                aria-label="Auftragssuche löschen"
                title="Suche löschen"
                disabled={disabled}
                onClick={() => {
                  setQuery('');
                  input.current?.focus();
                }}
              >
                <span aria-hidden="true">×</span>
              </button>
            )}
          </div>
          <p id={`${id}-hint`} className="wh-field-hint">
            Namen oder ID eingeben, dann den passenden Auftrag antippen.
          </p>
          <p role="status" className="wh-field-hint">
            {!projects.length
              ? 'Keine Aufträge auf diesem Gerät verfügbar. Daten bei Verbindung aktualisieren.'
              : !terms.length && projects.length > 6
                ? `${projects.length} Aufträge verfügbar. Bitte Suche eingeben.`
                : !matches.length
                  ? 'Kein Auftrag gefunden. Schreibweise oder Auftrags-ID prüfen.'
                  : matches.length > 6
                    ? `${matches.length} Treffer · die ersten 6 werden angezeigt. Namen oder ID genauer eingeben.`
                    : `${matches.length} Treffer · passenden Auftrag auswählen.`}
          </p>
          {(terms.length > 0 || projects.length <= 6) && (
            <ul className="wh-project-results" aria-label="Gefundene Aufträge">
              {matches.slice(0, 6).map(({ project }) => (
                <li key={project.id}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => choose(project.id)}
                  >
                    <strong>{project.name}</strong>{' '}
                    <span>Auftrags-ID: {project.id}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {allowWithoutProject && (
            <button
              type="button"
              className="wh-back"
              disabled={disabled}
              onClick={() => choose('without-project')}
            >
              {optional
                ? 'Ohne Auftrag fortfahren'
                : 'Ohne Auftrag · Grund angeben'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
